import React, { createContext, useState, useContext, ReactNode } from 'react';
import useIdleTimerHook from './UseIdleTimerHook';

// Define the shape of the context value
interface IdleTimeoutContextType {
  isPrompted: boolean;
  getRemainingTime: () => number;
  timeout: number;
  onExtend: () => void;
  extendTime: number;
}

// Define the props for the provider component
interface IdleTimeoutProviderProps {
  children: ReactNode;
  timeout?: number; // in milliseconds
  timeToPromptBeforeTimeout?: number; // in milliseconds
  extendTime?: number; // in milliseconds
}

// Create the context with a null default value, as it will be provided by the component.
const IdleTimeoutContext = createContext<IdleTimeoutContextType | null>(null);

export const IdleTimeoutProvider = ({
  children,
  timeout = 1000 * 60 * 60, // 60 minutes
  timeToPromptBeforeTimeout = 30 * 1000, // 30 seconds
  extendTime = 0,
}: IdleTimeoutProviderProps) => {
  const [isPrompted, setIsPrompted] = useState<boolean>(false);
  const [isExtended, setIsExtended] = useState<boolean>(false);

  const onPrompt = (): void => {
    setIsPrompted(true);
    setIsExtended(false);
  };

  const onTimeout = () => {
    setIsPrompted(true);
    if (isExtended) {
      return;
    }
    window.location.reload();
  };

  const extendStay = (): void => {
    setIsPrompted(false);
    setIsExtended(true);
  };

  const { getRemainingTime } = useIdleTimerHook({
    timeout,
    timeToPromptBeforeTimeout,
    onTimeout,
    onTimeToPromptBeforeTimeout: onPrompt,
    isPrompted,
    extendTime,
    isTimeExtended: isExtended,
  });

  // --- Context Value ---

  const contextValue: IdleTimeoutContextType = {
    isPrompted,
    timeout,
    getRemainingTime,
    onExtend: extendStay,
    extendTime,
  };

  return (
    <IdleTimeoutContext.Provider value={contextValue}>
      {children}
    </IdleTimeoutContext.Provider>
  );
};

// Custom hook to use the context, providing a type-safe way to access its values.
export const useIdleTimeoutContext = (): IdleTimeoutContextType => {
  const context = useContext(IdleTimeoutContext);
  if (context === null) {
    throw new Error(
      'useIdleTimeoutContext must be used within an IdleTimeoutProvider',
    );
  }
  return context;
};
